import { useCallback } from "react";
import { useSetAtom } from "jotai";
import { useApi } from "./useApi";
import { isProcessingApplicationAtom } from "@/store/applicationAtoms";

const APPLICATIONS_ENDPOINT = "/applications";

export interface SubmitApplicationRequest {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    legalBusinessName: string;
    taxId: string;
    streetAddress: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    resaleCertificate: File;
    // Cloudflare Turnstile's single-use token (OB-06); absent while Turnstile is off.
    turnstileToken?: string;
}

export interface SubmitApplicationResponse {
    // The API owns the post-submit destination and returns it on the 202.
    // Optional because a 202 with a non-JSON body leaves it absent — callers
    // fall back to their own success route.
    redirectUrl?: string;
}

export const useApplicationApi = () => {
    const { callApi } = useApi();
    const setIsProcessing = useSetAtom(isProcessingApplicationAtom);

    const submitApplication = useCallback(
        async (
            data: SubmitApplicationRequest,
        ): Promise<SubmitApplicationResponse> => {
            setIsProcessing(true);

            try {
                const formData = new FormData();

                // Names must match SubmitApplicationCommandBody exactly — the
                // command binds it with [FromForm], hence the `Body.` prefix.
                formData.append("Body.FirstName", data.firstName);
                formData.append("Body.LastName", data.lastName);
                formData.append("Body.Email", data.email);
                formData.append("Body.PhoneNumber", data.phoneNumber);
                formData.append(
                    "Body.LegalBusinessName",
                    data.legalBusinessName,
                );
                formData.append("Body.TaxId", data.taxId);
                formData.append("Body.StreetAddress", data.streetAddress);
                formData.append("Body.City", data.city);
                formData.append("Body.State", data.state);
                formData.append("Body.ZipCode", data.zipCode);
                formData.append("Body.Country", data.country);
                formData.append(
                    "Body.ResaleCertificate",
                    data.resaleCertificate,
                );
                // Unprefixed: the API's form binder tries the bare name first,
                // case-insensitively, so this binds on the command or on Body.
                // No token, no part — the request is then exactly as before.
                if (data.turnstileToken) {
                    formData.append("turnstileToken", data.turnstileToken);
                }

                return await callApi<SubmitApplicationResponse>(
                    APPLICATIONS_ENDPOINT,
                    {
                        method: "POST",
                        body: formData,
                        contentType: "multipart",
                        // Public form — no signed-in user, so skip the bearer token.
                        skipAuth: true,
                        // The form renders its own inline message (an older API's 409
                        // for a known email needs copy the generic toast cannot give),
                        // so the toast would double up. Same posture as approveOrganization.
                        skipErrorNotification: true,
                    },
                );
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing],
    );

    return { submitApplication };
};
